/* eslint-disable no-param-reassign */
import _ from "lodash";
import moment from "moment-timezone";
import { useState } from "react";
import { toast } from "react-toastify";
import { callApi } from "../../api";
import { useAuth } from "../useAuth";

// GET single shift detail including all attributes, all finances and total cash total in the shift: protected/business/{id}/shift/{sid}/get
// POST update a shift: protected/business/{id}/shift/{sid}/update Body: {starting_balance, closing_balance, status, balanced, started_at, closed_at, attributes: [{id,key,value,delete}]}
// POST add a finance: protected/business/{id}/finance/add  Body: {shift_id, employee_id, day, category, type, amount, description}
// POST update finance: protected/business/{id}/finance/{fid}/update Body same as “add”
// POST delete finance: protected/business/{id}/finance/{fid}/delete

const LOADING_TYPES = {
  FETCH_SHIFT_REPORTS: "fetch_shift_reports",
  GET_DRAWER_LOGS: "get_drawer_logs",
};

const dateFormat = "YYYY-MM-DD";
const prettyFormat = "ddd MMM D, YYYY";
const dbFormat = "YYYY-MM-DD HH:mm:ss";

const useShiftReports = () => {
  const [shiftReports, setShiftReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const { selectedBusiness, selectedBusinessDetail } = useAuth();

  const shiftParser = (shift) => {
    let val;
    switch (shift.type) {
      case "JSON":
        val = shift.value ? JSON.parse(shift.value) : null;
        break;
      case "INT":
        val = shift.value && parseInt(shift.value, 10);
        break;
      default:
        val = shift.value;
    }
    return val;
  };

  const financeTotal = (finances = []) => {
    let total = 0;
    finances.forEach((x) => {
      if (x.category !== "CASHDROP")
        total += x.isDebit ? Math.abs(x.amount) : -Math.abs(x.amount);
    });
    return total;
  };

  const calculateOffset = (shift) => {
    const cashsalesAttr = shift?.shiftattributes?.find(
      (x) => x.key === "salescash"
    );
    const closingBalanceAttr = shift?.shiftattributes?.find(
      (x) => x.key === "cashatclosing"
    );
    const cashSales = cashsalesAttr ? shiftParser(cashsalesAttr) : 0;
    const finTotal = financeTotal(shift.finances);
    const startingBalance = shift?.starting_balance || 0;
    const closingBalance = closingBalanceAttr
      ? shiftParser(closingBalanceAttr)
      : 0;

    return closingBalance - (cashSales + finTotal + startingBalance);
  };

  const parseShiftAttributes = (attrs, employees) => {
    const addedCashiers = _.filter(attrs, { key: "cashier added at closing" });
    const removedCashiers = _.filter(attrs, {
      key: "cashier removed at closing",
    });
    const allCashiers = _.uniqBy(
      _.filter(attrs, { key: "cashier" })
        .concat(addedCashiers)
        .concat(removedCashiers),
      "value"
    );
    const attrList = [
      {
        name: "Opening Denominations",
        id: "denominations",
      },
      {
        name: "Closing Denominations",
        id: "closingdenominations",
      },
      {
        name: "Notes",
        id: "notes",
      },
      {
        name: "Cash Offset",
        id: "cashoffset",
      },
      {
        name: "Shift Cash Sales",
        id: "salescash",
      },
      {
        name: "Cash at Closing",
        id: "cashatclosing",
      },
      {
        name: "Balance from Previous Shift",
        id: "lastbalance",
      },
    ];
    const cashiers = _.compact(
      _.map(allCashiers, (emp) => {
        const gotCashier = _.find(employees, { id: shiftParser(emp) });
        if (gotCashier) {
          return {
            id: gotCashier.id,
            name:
              gotCashier.name || gotCashier.nickname || gotCashier.full_name,
          };
        }
        return null;
      })
    );
    const obj = {};
    _.each(cashiers, (cashier) => {
      if (_.find(addedCashiers, (added) => +added.value === cashier.id)) {
        cashier.added = true;
      } else if (
        _.find(removedCashiers, (removed) => +removed.value === cashier.id)
      ) {
        cashier.removed = true;
      }
    });
    obj.staff = cashiers;
    _.each(attrList, (attr) => {
      const gotAttr = _.find(attrs, { key: attr.id });
      if (gotAttr) {
        obj[attr.id] = {
          name: attr.name,
          value: shiftParser(gotAttr),
        };
      }
    });
    return obj;
  };

  const getFinTotal = (finances) => {
    const groupByType = _.groupBy(finances, "category");
    const finArr = [];
    _.each(groupByType, (v, k) => {
      finArr.push({
        id: k,
        total:
          _.sumBy(v, (item) => (item.isDebit ? item.amount : -item.amount)) ||
          0,
      });
    });
    return finArr;
  };

  const updateShiftData = (shifts, total) => {
    const firstShift = _.minBy(shifts, (s) =>
      moment(s.started_at || s.created_at, dbFormat).valueOf()
    );
    const lastShift = _.maxBy(shifts, (s) =>
      moment(
        s.status === "CLOSED" ? s.closed_at : s.updated_at,
        dbFormat
      ).valueOf()
    );
    const startingBal = (firstShift && firstShift.starting_balance) || 0;
    const endingBal = (lastShift && lastShift.closing_balance) || 0;
    const cashSales = _.sumBy(
      shifts,
      (s) => (s.salescash && s.salescash.value) || 0
    );
    const bal = startingBal + cashSales + total;
    let staff = _.filter(firstShift.staff, (s) => !s.removed);
    if (lastShift && lastShift.id !== firstShift.id) {
      staff = _.uniqBy(
        staff.concat(_.filter(lastShift.staff, (s) => !s.removed)),
        "id"
      );
    }
    return {
      startTime:
        firstShift &&
        moment
          .utc(firstShift.started_at || firstShift.created_at, dbFormat)
          .tz(selectedBusinessDetail.business.timezone)
          .format("h:mmA"),
      endTime:
        lastShift &&
        moment
          .utc(lastShift.closed_at || lastShift.updated_at, dbFormat)
          .tz(selectedBusinessDetail.business.timezone)
          .format("h:mmA"),
      shiftClosed: lastShift && !!lastShift.closed_at,
      startingBalance: startingBal,
      closingBalance: endingBal,
      txTotal: total,
      cash: cashSales,
      balanced: bal === endingBal,
      cashOffset:
        _.sumBy(shifts, (s) => (s.cashoffset && s.cashoffset.value) || 0) || 0,
      over: endingBal > bal,
      short: endingBal < bal,
      cashiers: _.map(staff, "name").join(", "),
    };
  };

  const format = (d, employees) => {
    const data = d || [];
    const tz =
      selectedBusinessDetail.business.timezone || "America/Los_Angeles";
    const byDay = _.groupBy(data, "day");
    let finHeadings = [];
    const dailyData = [];
    _.each(byDay, (v, day) => {
      _.each(v, (item) => {
        _.merge(
          item,
          parseShiftAttributes(item.shiftattributes, employees || [])
        );
        item.finTotal = getFinTotal(item.finances);
        const opened = moment.utc(item.started_at);
        const closed = moment.utc(item.closed_at || item.updated_at);
        const dur = closed.diff(opened, "minutes");
        item.durationText =
          dur > 60
            ? `${Math.floor(dur / 60)} Hr. ${Math.floor(dur % 60)} Min.`
            : `${dur} Min.`;
      });
      const fin = _.compact(_.flatten(_.map(v, "finances")));
      const finTotal = getFinTotal(fin);
      const txTotal = _.sumBy(finTotal, "total");
      finHeadings = _.uniqBy(finHeadings.concat(finTotal), "id");
      dailyData.push({
        day,
        dayPretty: moment.tz(day, dateFormat, tz).format(prettyFormat),
        allShifts: v,
        finTotal: finTotal || [],
        dayShift: updateShiftData(v, txTotal),
      });
    });
    const dailyReports = {};
    dailyReports.finHeadings = _.uniq(finHeadings);
    _.each(dailyData, (_d) => {
      _.each(dailyReports.finHeadings, (h) => {
        if (!_.find(_d.finTotal, { id: h.id })) {
          _d.finTotal.push({
            id: h.id,
            total: 0,
          });
        }
      });
    });
    dailyReports.shiftsList = dailyData;
    dailyReports.balanceTypes = {
      balanced:
        _.sumBy(dailyData, (item) =>
          item.dayShift && item.dayShift.balanced ? 1 : 0
        ) || 0,
      over:
        _.sumBy(dailyData, (item) =>
          item.dayShift && item.dayShift.over ? 1 : 0
        ) || 0,
      short:
        _.sumBy(dailyData, (item) =>
          item.dayShift && item.dayShift.short ? 1 : 0
        ) || 0,
    };
    dailyReports.shiftsList = dailyReports.shiftsList.map((s) => ({
      ...s,
      status: s.dayShift.balanced
        ? "Balanced"
        : s.dayShift.over
        ? "Over"
        : "Short",
    }));
    return {
      shiftReports: data,
      dailyReports,
    };
  };

  const getShiftReports = async (startDate, endDate) => {
    setLoading(LOADING_TYPES.FETCH_SHIFT_REPORTS);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/shifts?start=${startDate}&end=${endDate}`
    );
    if (res && res.status === true) {
      setShiftReports(res.data);
    } else {
      toast.error((res && res.message) || "Failed to fetch shift report!");
    }
    setLoading(null);
  };

  const getDrawerLogs = async (day) => {
    setLoading(LOADING_TYPES.GET_DRAWER_LOGS);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/drawerlog/${day}`
    );
    setLoading(null);
    return res;
  };

  const updateSingleShiftData = async (sid, data) => {
    const body = {
      starting_balance: data?.starting_balance,
      closing_balance: data?.closing_balance,
      status: data?.status,
      balanced: data?.balanced,
      started_at: data?.started_at,
      closed_at: data?.closed_at,
      attributes: data?.attributes,
      day: data?.day,
    };
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/shift/${sid}/update`,
      "POST",
      body
    );
    if (res && res.status === true) {
      setShiftReports(
        shiftReports.map((el) => {
          if (el.id === sid) return res.data;
          return el;
        })
      );
      toast.success("Shift data updated successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to fetch shift data");
  };

  const addFinance = async ({
    shiftId,
    employeeId,
    day,
    category,
    type,
    amount,
    description,
  }) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/finance/add`,
      "POST",
      {
        shift_id: shiftId,
        employee_id: employeeId,
        day,
        category,
        type,
        amount,
        description,
      }
    );
    if (res && res.status === true) {
      const temp = shiftReports.find((el) => el.id === shiftId);
      if (!temp) return;
      const { finances } = temp;
      temp.finances = [...finances, res.data];
      const cashOffset = calculateOffset(temp);
      const offsetData = temp.shiftattributes?.find(
        (x) => x.key === "cashoffset"
      );
      await updateSingleShiftData(shiftId, {
        attributes: [
          { id: offsetData?.id, key: "cashoffset", value: cashOffset },
        ],
      });

      // setShiftReports(
      //   shiftReports.map((el) => {
      //     if (el.id === shiftId) return temp;
      //     return el;
      //   })
      // );
      // toast.success("Finance added successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to add finance!");
  };

  const updateFinance = async ({
    shiftId,
    employeeId,
    fid,
    day,
    category,
    type,
    amount,
    description,
  }) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/finance/${fid}/update`,
      "POST",
      {
        id: fid,
        shift_id: shiftId,
        employee_id: employeeId,
        day,
        category,
        type,
        amount,
        description,
      }
    );
    if (res && res.status === true) {
      const temp = shiftReports.find((el) => el.id === shiftId);
      if (!temp) return;
      const { finances } = temp;
      const newFinances = finances.map((el) => {
        if (el.id === fid) {
          return res.data;
        }
        return el;
      });
      temp.finances = newFinances;
      const cashOffset = calculateOffset(temp);
      const offsetData = temp.shiftattributes?.find(
        (x) => x.key === "cashoffset"
      );
      await updateSingleShiftData(shiftId, {
        attributes: [
          { id: offsetData?.id, key: "cashoffset", value: cashOffset },
        ],
      });
      // setShiftReports(
      //   shiftReports.map((el) => {
      //     if (el.id === shiftId) return temp;
      //     return el;
      //   })
      // );
      // toast.success("Finance updated successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to update finance!");
  };

  const deleteFinance = async (fid, shiftId) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/finance/${fid}/delete`,
      "POST"
    );
    if (res && res.status === true) {
      const temp = shiftReports.find((el) => el.id === shiftId);
      if (!temp) return;
      const { finances } = temp;
      const newFinances = finances.filter((el) => {
        if (el.id === fid) {
          return false;
        }
        return true;
      });
      temp.finances = newFinances;
      const cashOffset = calculateOffset(temp);
      const offsetData = temp.shiftattributes?.find(
        (x) => x.key === "cashoffset"
      );
      await updateSingleShiftData(shiftId, {
        attributes: [
          { id: offsetData?.id, key: "cashoffset", value: cashOffset },
        ],
      });
      // setShiftReports(
      //   shiftReports.map((el) => {
      //     if (el.id === shiftId) return temp;
      //     return el;
      //   })
      // );
      // toast.success("Finance deleted successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to delete finance!");
  };

  const updateClosingData = async ({
    closingbalance,
    cashatclosing,
    cashdrop,
    shift,
  }) => {
    const cashDropFin = shift.finances?.find((x) => x.category === "CASHDROP");

    await callApi(
      cashDropFin
        ? `/protected/business/${selectedBusiness.id}/finance/${
            cashDropFin.id
          }/${parseFloat(cashdrop) ? "update" : "delete"}`
        : `/protected/business/${selectedBusiness.id}/finance/add`,
      "POST",
      {
        shift_id: shift.id,
        day: cashDropFin?.day || moment().format("YYYY-MM-DD"),
        category: "CASHDROP",
        type: "CREDIT",
        amount: cashdrop,
        description: "Cashdrop at closing",
      }
    );

    const cashsales = shift?.salescash?.value || 0;

    const cashAtClosingAttr = shift?.shiftattributes?.find(
      (x) => x.key === "cashatclosing"
    );

    const cashOffset = shift?.shiftattributes?.find(
      (x) => x.key === "cashoffset"
    );

    const totalFinances = financeTotal(shift?.finances);
    // shift?.finances
    //   ?.map((x) => {
    //     const amount = +x.amount;
    //     if (x.isDebit) {
    //       return Math.abs(amount) || 0;
    //     }
    //     return -1 * Math.abs(amount) || 0;
    //   })
    //   .reduce((a, b) => a + b, 0) || 0;
    const startingBalance = +shift?.starting_balance || 0;

    const offset =
      +cashatclosing - startingBalance - totalFinances - cashsales || 0;

    await updateSingleShiftData(shift.id, {
      closing_balance: closingbalance,
      attributes: [
        {
          id: cashAtClosingAttr?.id,
          key: "cashatclosing",
          value: cashatclosing,
        },
        {
          id: cashOffset?.id,
          key: "cashoffset",
          value: offset,
        },
      ],
    });
  };

  const updateCashierData = async (shift, keys = []) => {
    const { shiftattributes } = shift;
    let attributes = [];
    keys.forEach((obj) => {
      const filteredAttributes = shiftattributes?.filter(
        (el) => el.key === obj.key
      );
      attributes = attributes.concat(
        obj.value
          .filter((el) => {
            const data = filteredAttributes.find((x) => el === shiftParser(x));
            return !data;
          })
          .map((el) => ({ key: obj.key, value: el }))
      );
      if (filteredAttributes)
        attributes = attributes.concat(
          filteredAttributes
            .filter((el) => !obj.value.includes(shiftParser(el)))
            .map((el) => ({ id: el.id, key: obj.key, delete: true }))
        );
    });
    if (attributes.length)
      await updateSingleShiftData(shift.id, {
        attributes,
      });
  };

  const getShiftData = async (sid) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/shift/${sid}/get`
    );
    if (res && res.status === true) {
      return res.data;
    }
    toast.error((res && res.message) || "Failed to fetch shift data");
    return null;
  };

  return {
    shiftReports,
    getShiftReports,
    getDrawerLogs,
    loadingFetch: loading === LOADING_TYPES.FETCH_SHIFT_REPORTS,
    loadingDrawerLog: loading === LOADING_TYPES.GET_DRAWER_LOGS,
    format,
    addFinance,
    updateFinance,
    deleteFinance,
    getShiftData,
    updateShiftData: updateSingleShiftData,
    updateClosingData,
    updateCashierData,
  };
};

export default useShiftReports;
